import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }; // Monday
export const fmtShort = (isoDate) => new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-SG", { day: "2-digit", month: "short" });
export const money = (n) => `$${Number(n || 0).toLocaleString("en-SG", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Visible range: month view = 6 full weeks (prev/next month tails); week view = 7 days + 3-day buffer each side
export function rangeFor(view, anchor) {
  if (view === "week") { const s = startOfWeek(anchor); return { start: addDays(s, -3), end: addDays(s, 9), days: Array.from({ length: 7 }, (_, i) => addDays(s, i)) }; }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const s = startOfWeek(first);
  return { start: s, end: addDays(s, 41), days: Array.from({ length: 42 }, (_, i) => addDays(s, i)) };
}

// Money summary for the period on screen (week = 7 days, month = the anchor month only). Jobs count once, on their scheduled date.
export function summarise(byDate, view, anchor, days) {
  const inPeriod = (d) => (view === "week" ? days.some((x) => iso(x) === d) : d.slice(0, 7) === iso(anchor).slice(0, 7));
  const out = { jobs: 0, total: 0, paid: 0, unpaid: 0, ongoing: 0, collected: 0, drafts: 0 };
  Object.entries(byDate).forEach(([d, jobs]) => { if (!inPeriod(d)) return; jobs.forEach((j) => {
    if (j.draft) { out.drafts++; return; } // quotes booked ahead are not revenue yet
    const t = Number(j.total || 0); out.jobs++; out.total += t; out[j.paid ? "paid" : "unpaid"] += t; out[j.stage === "ongoing" ? "ongoing" : "collected"]++;
  }); });
  return out;
}

export function useCalendarJobs(view, anchor, filters) {
  const [byDate, setByDate] = useState({});
  const [config, setConfig] = useState({ job_stages: [], daily_capacity: 0, sunday_open: false });
  const [loading, setLoading] = useState(true);
  const range = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: iso(range.start), end_date: iso(range.end), search: filters.search || "", stage: filters.stage || "", payment: filters.payment || "" };
      const { data } = await axios.get(`${API}/calendar/jobs`, { params });
      setByDate(data.by_date);
    } catch (e) { toast.error("Could not load calendar", { description: e?.response?.data?.detail }); }
    finally { setLoading(false); }
  }, [range, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/calendar/filters`).then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  // One card per job, on its scheduled day only
  const perDay = byDate;

  const summary = useMemo(() => summarise(byDate, view, anchor, range.days), [byDate, view, anchor, range]);

  const reschedule = async (job, dateIso) => {
    if (job.scheduled_date === dateIso) return;
    const locked = job.stage === "collected" || job.invoice_no;
    if (locked && !window.confirm(`${job.plate} is ${job.invoice_no ? "invoiced" : "completed"}. Move it to ${fmtShort(dateIso)} anyway?`)) return;
    try {
      await axios.patch(`${API}/jobs/${job.id}/schedule`, { scheduled_date: dateIso });
      toast.success(`${job.plate || job.quote_no} rescheduled to ${new Date(`${dateIso}T00:00:00`).toLocaleDateString("en-SG", { day: "2-digit", month: "short", year: "numeric" })}.`);
      load();
    } catch (e) { toast.error("Could not reschedule", { description: e?.response?.data?.detail }); }
  };

  const patchJob = async (job, patch) => {
    try { const { data } = await axios.patch(`${API}/jobs/${job.id}`, patch); load(); return data; }
    catch (e) { toast.error("Could not update job", { description: e?.response?.data?.detail }); return false; }
  };

  return { byDate, perDay, config, summary, loading, range, reload: load, reschedule, patchJob };
}

const QUOTE_STAGE = { key: "quote", name: "Quote 报价", colour: "#94a3b8" };
export const stageOf = (config, key) => (key === "quote" ? QUOTE_STAGE : config.job_stages?.find((s) => s.key === key) || { key: "ongoing", name: "Ongoing 进行中", colour: "#f59e0b" });
